import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type DeletionNotification = 'none' | 'email' | 'sms';
export interface DeletionReceipt { token: string; notification: DeletionNotification; accepted: boolean; accountId: string; }
export interface DeletionStatus { status: 'processing' | 'completed'; notification: DeletionNotification; }

@Injectable({ providedIn: 'root' })
export class AccountDeletionService {
  private readonly url = environment.apiHost + '/AccountDeletion';
  constructor(private http: HttpClient) {}
  options() { return this.http.get<{email:boolean;sms:boolean}>(this.url + '/options').pipe(timeout(5000)).toPromise(); }
  receipt(tenant: string): DeletionReceipt | null {
    try {
      const saved = JSON.parse(sessionStorage.getItem('CSDeletion:' + tenant));
      return /^[a-f0-9]{64}$/.test(saved?.token) && ['none','email','sms'].includes(saved?.notification) ? saved : null;
    } catch { return null; }
  }
  private save(tenant: string, receipt: DeletionReceipt) {
    try { sessionStorage.setItem('CSDeletion:' + tenant, JSON.stringify(receipt)); } catch { /* In-memory receipt still permits waiting. */ }
  }
  async start(tenant: string, notification: DeletionNotification, accountId: string): Promise<DeletionReceipt> {
    const prior = this.receipt(tenant);
    const receipt: DeletionReceipt = (prior?.accountId === accountId ? prior : null) || { token: Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2,'0')).join(''), notification, accepted:false, accountId };
    // Persist before POST so a lost response does not lose access to status.
    if (!receipt.accepted) receipt.notification = notification;
    this.save(tenant, receipt);
    try {
      const response = await this.http.post<{status:string;notification:DeletionNotification}>(this.url + '/request', {token:receipt.token, notification:receipt.notification}).pipe(timeout(5000)).toPromise();
      if (response?.status !== 'accepted') throw new Error('Deletion request was not confirmed');
      receipt.notification = response.notification;
    } catch (error) {
      // A timed-out POST may already have committed. The capability check is
      // anonymous because the original login is disabled after acceptance.
      try { receipt.notification = (await this.status(receipt)).notification; }
      catch { throw error; }
    }
    receipt.accepted = true;
    this.save(tenant, receipt);
    return receipt;
  }
  status(receipt: DeletionReceipt): Promise<DeletionStatus> {
    return this.http.post<DeletionStatus>(this.url + '/status', {token:receipt.token}).pipe(timeout(10000)).toPromise();
  }
  forget(tenant: string) { try { sessionStorage.removeItem('CSDeletion:' + tenant); } catch {} }
}
